import { supabase } from '../supabaseClient'

class PaymentService {
  getPaginatedPayments = async (
    page: number,
    term?: string,
    status?: string,
    refundStatus?: string,
    supplierUuid?: string,
    sortOptions?: { sortField: string | null; sortDirection: 'asc' | 'desc' },
  ): Promise<any> => {
    try {
      const count = await this.countPayments(term, status, refundStatus, supplierUuid)
      const rangeStart = (page - 1) * 5
      const rangeEnd = page * 5 - 1

      let query = supabase
        .from('payments')
        .select(
          `
          *,
          simple_profile!payments_created_by_fkey (email),
          bookings!inner(
            payment_uuid,
            bookable_uuid,
            bookable:bookable_uuid (
              supplier_uuid
            )
          )
        `,
        )
        .range(rangeStart, rangeEnd)

      if (term) {
        if (this.isUUID(term)) {
          query = query.eq('uuid', term)
        } else if (this.isPaymentProviderId(term)) {
          query = query.eq('payment_provider_id', term)
        } else {
          query = query.or(`created_by.in.(${await this.getProfileUuidsByEmail(term)})`)
        }
      }

      if (status) {
        query = query.eq('payment_status', status)
      }

      if (refundStatus) {
        query = query.eq('refund_status', refundStatus)
      }

      if (supplierUuid) {
        query = query.eq('bookings.bookable:bookable_uuid.supplier_uuid', supplierUuid)
      }

      if (sortOptions?.sortField && sortOptions.sortDirection) {
        query = query.order(sortOptions.sortField, {
          ascending: sortOptions.sortDirection === 'asc',
        })
      }

      const { data, error } = await query

      if (error) {
        return { data: [], count, totalPages: Math.ceil(count / 5) }
      }

      const totalPages = Math.ceil(count / 5)

      return { data: data || [], count, totalPages }
    } catch (error) {
      console.error('Error in paginated payments:', error)
      return { data: [], count: 0, totalPages: 0 }
    }
  }

  countPayments = async (
    term?: string,
    status?: string,
    refundStatus?: string,
    supplierUuid?: string,
  ): Promise<number> => {
    try {
      let query = supabase.from('payments').select(
        `
          *,
          simple_profile!payments_created_by_fkey (email),
          bookings!inner(
            payment_uuid,
            bookable_uuid,
            bookable:bookable_uuid (
              supplier_uuid
            )
          )
        `,
        { count: 'exact' },
      )

      if (term) {
        if (this.isUUID(term)) {
          query = query.eq('uuid', term)
        } else if (this.isPaymentProviderId(term)) {
          query = query.eq('payment_provider_id', term)
        } else {
          query = query.or(`created_by.in.(${await this.getProfileUuidsByEmail(term)})`)
        }
      }

      if (status) {
        query = query.eq('payment_status', status)
      }

      if (refundStatus) {
        query = query.eq('refund_status', refundStatus)
      }

      if (supplierUuid) {
        query = query.eq('bookings.bookable:bookable_uuid.supplier_uuid', supplierUuid)
      }

      const { count, error } = await query

      if (error) {
        console.error('Error fetching total payment count:', error)
        return 0
      }

      return count || 0
    } catch (e) {
      console.error('Error counting payments:', e)
      return 0
    }
  }

  getProfileUuidsByEmail = async (term: string): Promise<string> => {
    try {
      const { data, error } = await supabase
        .from('simple_profile')
        .select('uuid')
        .ilike('email', `%${term}%`)

      if (error) {
        console.error('Error fetching profile UUIDs:', error)
        return ''
      }

      return data?.map((profile) => profile.uuid).join(',') || ''
    } catch (error) {
      console.error('Error in getProfileUuidsByEmail:', error)
      return ''
    }
  }

  isUUID = (term: string): boolean => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    return uuidRegex.test(term)
  }

  isPaymentProviderId = (term: string): boolean => {
    return term.startsWith('cus')
  }
}

const paymentService = new PaymentService()

export default paymentService
